/* eslint-disable complexity */
import { AdaptiveDialog } from '@/components/blocks/AdaptiveDialog';
import { useBeneficiarySubmissionContext } from '@/components/blocks/BeneficiarySubmissionContext';
import { CartSetupRender } from '@/components/blocks/CartRender';
import { DpButton } from '@/components/blocks/DpLibrary/DpButton';
import { DpOutlinedInput } from '@/components/blocks/OutlinedInput';
import { ProductsAvatarGroup } from '@/components/blocks/ProductsAvatarGroup';
import { useMobileHeaderQuery } from '@/components/content/Header';
import { EMPTY_FUNCTION, EMPTY_PROMISE_FUNCTION } from '@/data/config/FUNC';
import { HOME_URL } from '@/data/config/HOME';
import { useCart } from '@/data/Content/Cart';
import { useDpCatalogoDistributor, useDpCatalogoSendOrder } from '@/data/Content/DpCatalogo';
import { REG_EX } from '@/utils/address';
import { formatPhoneNumber } from '@/utils/formatPhoneNumber';
import { formatPrice } from '@/utils/formatPrice';
import { ErrorState, useForm } from '@/utils/useForm';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import { Box, Stack, Typography } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';

export const BeneficiarySubmissionModal = () => {
	const { data: order, mutateCart } = useCart();
	const { open, onClose } = useBeneficiarySubmissionContext();
	const [thankYou, setThankYou] = useState(false);
	const isMobile = useMobileHeaderQuery();
	const { data: distributor } = useDpCatalogoDistributor();
	const { sendOrder } = useDpCatalogoSendOrder();

	const initialValues = useMemo(
		() => ({
			clientFullName: '',
			clientEmail: '',
			clientPhone: '',
			isLoading: false,
		}),
		[]
	);

	type FormErrorState = ErrorState<Omit<typeof initialValues, 'isLoading'>>;

	const [error, setError] = useState<FormErrorState>({
		clientFullName: false,
		clientEmail: false,
		clientPhone: false,
	});

	const form = useForm(initialValues);

	const onSubmit = useCallback(async () => {
		const hasError: FormErrorState = {
			clientFullName: false,
			clientEmail: false,
			clientPhone: false,
		};

		if (
			!form.values.clientFullName ||
			!REG_EX.FULL_NAME.test(form.values.clientFullName) ||
			form.values.clientFullName.length < 3
		) {
			hasError.clientFullName = true;
		}

		if (
			!form.values.clientEmail ||
			!REG_EX.EMAIL.test(form.values.clientEmail) ||
			form.values.clientEmail.length < 3
		) {
			hasError.clientEmail = true;
		}

		const clientPhone = form.values.clientPhone.replace(/\D/g, '');
		if (!clientPhone || clientPhone.length < 10) {
			hasError.clientPhone = true;
		}

		if (Object.values(hasError).some((value) => value)) {
			setError(hasError);
			return;
		}

		form.onNamedValueChange('isLoading', true);
		const response = await sendOrder({
			orderId: order?.orderId ?? '',
			token: distributor?.token ?? '',
			clientFullName: form.values.clientFullName,
			clientEmail: form.values.clientEmail,
			clientPhone,
		});

		if (response.success) {
			setThankYou(true);
			await mutateCart();
		}

		form.onNamedValueChange('isLoading', false);
	}, [form, sendOrder, distributor, order, mutateCart]);

	const handlePhoneChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const value = event.target.value.replace(/\D/g, '');
			form.onNamedValueChange('clientPhone', value);
			setError((prev) => ({ ...prev, clientPhone: false }));
		},
		[form]
	);

	const handleInputChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			form.handleInputChange(event);
			setError((prev) => ({ ...prev, [event.target.name]: false }));
		},
		[form]
	);

	const handleInputBlur = (event: React.FocusEvent<HTMLInputElement>) => {
		setError((prev) => ({ ...prev, [event.target.name]: false }));
	};

	const handleClose = () => {
		onClose();
		setThankYou(false);
		form.resetForm();
	};

	return (
		<AdaptiveDialog open={open} onClose={handleClose}>
			{!distributor?.token || !distributor?.logonId ? (
				<Box>
					<Stack
						sx={{
							flexDirection: 'column',
							alignItems: 'flex-start',
							mb: { xs: 2, md: 0 },
						}}
					>
						<Stack direction="row" alignItems="center" gap={1} mb={2}>
							<WarningIcon sx={{ fontSize: 32, color: '#FFA500' }} />
							<Typography
								sx={{ fontSize: '24px', fontWeight: 800, lineHeight: '33px', color: '#2C2C2C' }}
							>
								{'Contacta a tu distribuidor'}
							</Typography>
						</Stack>
						<Typography
							sx={{
								fontSize: '15px',
								lineHeight: '20px',
								color: '#2C2C2C',
								marginBottom: '24px',
							}}
						>
							{
								'Es necesario que tu distribuidor te comparta la información de su tienda para que puedas realizarle un pedido.'
							}
						</Typography>
						<DpButton variant="primary" href={HOME_URL} fullWidth onClick={onClose}>
							{'Ir a la tienda'}
						</DpButton>
					</Stack>
				</Box>
			) : thankYou ? (
				<Box>
					<Stack
						sx={{
							flexDirection: 'column',
							alignItems: 'flex-start',
						}}
					>
						<Stack direction="row" alignItems="center" gap={1} mb={2}>
							<CheckCircleIcon sx={{ fontSize: 40, color: '#008000' }} />
							<Typography
								sx={{ fontSize: '24px', fontWeight: 800, lineHeight: '33px', color: '#2C2C2C' }}
							>
								{'Gracias por tu compra'}
							</Typography>
						</Stack>
						<Typography
							sx={{
								fontSize: '15px',
								lineHeight: '20px',
								color: '#2C2C2C',
								marginBottom: '24px',
							}}
						>
							{'Tu pedido ha sido enviado a tu distribuidor '}
							<Typography component="span" sx={{ fontWeight: 800 }}>
								{distributor?.name || ''} {distributor?.lastName || ''}
							</Typography>{' '}
							{'para que pueda contactarte.'}
						</Typography>
						<DpButton variant="primary" href={HOME_URL} fullWidth onClick={onClose}>
							{'Ir a la tienda'}
						</DpButton>
					</Stack>
				</Box>
			) : (
				<CartSetupRender
					order={order}
					onDeleteCart={EMPTY_PROMISE_FUNCTION}
					onClose={EMPTY_FUNCTION}
				>
					{({ productsGroup, grandTotal, products }) => (
						<Box component="form" ref={form.formRef} onSubmit={form.handleSubmit(onSubmit)}>
							<Stack
								sx={{
									flexDirection: 'column',
									alignItems: 'flex-start',
									marginBottom: '24px',
								}}
							>
								<Typography
									sx={{
										fontSize: '24px',
										fontWeight: 800,
										lineHeight: '33px',
										color: '#2C2C2C',
									}}
								>
									{'Ya casi terminas'}
								</Typography>
								<Typography
									sx={{
										fontSize: '15px',
										lineHeight: '20px',
										color: '#2C2C2C',
										marginBottom: '10px',
									}}
								>
									{'Ingresa tu información para enviarla a tu distribuidor '}
									<Typography component="span" sx={{ fontWeight: 800 }}>
										{distributor?.name || ''} {distributor?.lastName || ''}
									</Typography>
								</Typography>
								<Stack direction="row" spacing={2}>
									<ProductsAvatarGroup showMaxItems={3} items={productsGroup} />
									<Stack>
										<Typography>{`${products.length} ${
											products.length === 1 ? 'producto' : 'productos'
										}`}</Typography>
										<Typography sx={{ display: 'flex', flexDirection: 'row', gap: 0.5 }}>
											<Typography sx={{ fontSize: '15px', fontWeight: 800 }}>{'Total:'}</Typography>
											<Typography sx={{ fontSize: '15px', fontWeight: 500 }}>
												{formatPrice('es-MX', 'MXN', grandTotal)}
											</Typography>
										</Typography>
									</Stack>
								</Stack>
							</Stack>
							<Stack sx={{ gap: 2, marginBottom: isMobile ? '38px' : '0px' }}>
								<DpOutlinedInput
									name="clientFullName"
									label="Nombre completo"
									fullWidth
									inputProps={{
										maxLength: 100,
										pattern: REG_EX.FULL_NAME.source,
									}}
									autoComplete="name"
									value={form.values.clientFullName}
									error={error.clientFullName}
									helperText={error.clientFullName ? 'Nombre completo inválido' : ''}
									onChange={handleInputChange}
									onBlur={handleInputBlur}
								/>
								<DpOutlinedInput
									name="clientPhone"
									label="Teléfono"
									type="tel"
									value={formatPhoneNumber(form.values.clientPhone)}
									inputProps={{ maxLength: 12, pattern: REG_EX.PHONE.source }}
									autoComplete="tel"
									fullWidth
									error={error.clientPhone}
									helperText={error.clientPhone ? 'Teléfono inválido' : ''}
									onChange={handlePhoneChange}
									onBlur={handleInputBlur}
								/>
								<DpOutlinedInput
									name="clientEmail"
									label="Correo electrónico"
									autoComplete="email"
									inputProps={{
										maxLength: 100,
										type: 'email',
										pattern: REG_EX.EMAIL.source,
									}}
									fullWidth
									value={form.values.clientEmail}
									error={error.clientEmail}
									helperText={error.clientEmail ? 'Correo electrónico inválido' : ''}
									onChange={handleInputChange}
									onBlur={handleInputBlur}
								/>
								<DpButton
									variant="primary"
									fullWidth
									type="submit"
									disabled={form.values.isLoading || form.submitting}
								>
									{'Enviar'}
								</DpButton>
							</Stack>
						</Box>
					)}
				</CartSetupRender>
			)}
		</AdaptiveDialog>
	);
};
